'use client';

import React from 'react';

// Minimal, dependency-free markdown renderer (no new npm deps so the running dev
// server is untouched). Handles headings, bold/italic/code, links, and ordered/
// unordered lists — enough for the chatbot's answers. HTML is escaped before any
// transform, so inline rendering via dangerouslySetInnerHTML is safe here.

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function inline(text: string): string {
  let s = escapeHtml(text);
  // links [label](url) -> open in new tab. Accepts absolute URLs and root-relative
  // paths (e.g. /devis-mutuelle-sante) so the comparator CTA is clickable.
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">$1</a>',
  );
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
  s = s.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-sm">$1</code>');
  return s;
}

export default function MarkdownLite({ text }: { text: string }) {
  const lines = (text || '').replace(/\r/g, '').split('\n');
  const blocks: React.ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  let key = 0;

  const flushPara = () => {
    if (para.length) {
      const html = inline(para.join(' '));
      blocks.push(<p key={key++} className="my-1.5" dangerouslySetInnerHTML={{ __html: html }} />);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const items = list.items.map((it, i) => (
        <li key={i} className="ml-1" dangerouslySetInnerHTML={{ __html: inline(it) }} />
      ));
      blocks.push(
        list.ordered ? (
          <ol key={key++} className="list-decimal ml-5 my-1.5 space-y-0.5">{items}</ol>
        ) : (
          <ul key={key++} className="list-disc ml-5 my-1.5 space-y-0.5">{items}</ul>
        ),
      );
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    const ul = line.match(/^\s*[-*•]\s+(.*)$/);
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);

    if (line.trim() === '') {
      flushPara();
      flushList();
    } else if (heading) {
      flushPara();
      flushList();
      blocks.push(
        <h4 key={key++} className="font-semibold mt-2 mb-1" dangerouslySetInnerHTML={{ __html: inline(heading[2]) }} />,
      );
    } else if (ul) {
      flushPara();
      if (!list || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(ul[1]);
    } else if (ol) {
      flushPara();
      if (!list || !list.ordered) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(ol[1]);
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();

  return <div className="text-sm leading-relaxed text-gray-800">{blocks}</div>;
}
